import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, Tooltip } from '@fly/core';
import { ISuggestion } from '@fly/shared';
import classNames from 'classnames';

import styles from '../../FlyChat.module.less';
import { FlyChatSuggestions } from '../FlyChatSuggestions/FlyChatSuggestions';

interface FlyChatFooterProps {
  onSendMessage?: (message: string, display?: boolean) => void;
  onEnqueueMessage?: (message: string) => void;
  onDequeueMessage?: () => void;
  onRemoveQueuedMessage?: (index: number) => void;
  onClearQueuedMessages?: () => void;
  isLoading?: boolean;
  isStreaming?: boolean;
  streamError?: string | null;
  suggestions?: Array<ISuggestion>;
  queuedMessages?: string[];
}

export const FlyChatFooter: React.FC<FlyChatFooterProps> = ({
  onSendMessage,
  onEnqueueMessage,
  onDequeueMessage,
  onRemoveQueuedMessage,
  onClearQueuedMessages,
  suggestions = [],
  isLoading = false,
  isStreaming = false,
  streamError = null,
  queuedMessages = [],
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [isQueueOpen, setIsQueueOpen] = useState(true);
  const autoNavKey: string =
    import.meta.env.VITE_LOCAL_STORAGE_SET_AUTON_NAV || 'auto-navigation';
  let isLocalAutoNaveSet = localStorage.getItem(autoNavKey);
  if (!isLocalAutoNaveSet) {
    localStorage.setItem(autoNavKey, 'true');
    isLocalAutoNaveSet = 'true';
  }
  const [isActive, setIsActive] = React.useState(
    isLocalAutoNaveSet?.toLowerCase() === 'true'
  );
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const prevIsStreamingRef = useRef(isStreaming);

  const sendOrQueueMessage = (messageToHandle: string) => {
    const trimmedMessage = messageToHandle.trim();
    if (!trimmedMessage || isLoading) return;

    if (isStreaming) {
      onEnqueueMessage?.(trimmedMessage);
      return;
    }

    onSendMessage?.(trimmedMessage);
  };

  const handleSendMessage = () => {
    sendOrQueueMessage(message);
    setMessage('');
    textAreaRef?.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAutoNavClick = () => {
    setIsActive((prev) => {
      const newState = !prev;
      localStorage.setItem(autoNavKey, String(newState));
      return newState;
    });
  };

  const handleSuggestionMessage = (suggestionMessage: string) => {
    sendOrQueueMessage(suggestionMessage);
  };

  const handleRemoveQueuedMessage = (index: number) => {
    onRemoveQueuedMessage?.(index);
  };

  const handleToggleQueue = () => {
    setIsQueueOpen((prev) => !prev);
  };

  const handleClearQueuedMessages = () => {
    onClearQueuedMessages?.();
  };

  useEffect(() => {
    if (textAreaRef.current) {
      const textarea = textAreaRef.current;
      textarea.style.height = '38px';
      const scrollHeight = textarea.scrollHeight;
      const newHeight = Math.min(scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, [message]);

  useEffect(() => {
    if (localStorage.getItem(autoNavKey) === 'true') {
      setIsActive(true);
    }
  }, [autoNavKey]);

  useEffect(() => {
    const wasStreaming = prevIsStreamingRef.current;
    const isStreamingDone = wasStreaming && !isStreaming;
    if (
      isStreamingDone &&
      !streamError &&
      queuedMessages.length > 0 &&
      !isLoading
    ) {
      onSendMessage?.(queuedMessages[0]);
      onDequeueMessage?.();
    }
    prevIsStreamingRef.current = isStreaming;
  }, [
    isLoading,
    isStreaming,
    onDequeueMessage,
    onSendMessage,
    queuedMessages,
    streamError,
  ]);

  useEffect(() => {
    if (!isLoading && !isStreaming) {
      setTimeout(() => {
        textAreaRef?.current?.focus();
      }, 0);
    }
  }, [isLoading, isStreaming]);

  return (
    <div className={styles.chatFooterWrapper} data-testid='chat-footer'>
      {queuedMessages.length > 0 && (
        <div className={styles.queueContainer} data-testid='chat-queue'>
          <div className={styles.queueHeader}>
            <Button
              size='1'
              variant='ghost'
              color='neutral'
              className={styles.queueToggleButton}
              data-testid='chat-queue-toggle'
              onClick={handleToggleQueue}
            >
              <span>
                {t('app.dashboard.chat.footer.queue_title', {
                  defaultValue: 'Queued Messages',
                })}
              </span>
              <span
                className={styles.queueCount}
                data-testid='chat-queue-count'
              >
                {t('app.dashboard.chat.footer.queue_count', {
                  defaultValue: '{{count}}',
                  count: queuedMessages.length,
                })}
              </span>
            </Button>
            <span className={styles.queueHeaderActions}>
              <Button
                size='1'
                variant='ghost'
                color='neutral'
                className={styles.queueClearButton}
                data-testid='chat-queue-clear-all'
                onClick={handleClearQueuedMessages}
              >
                {t('app.dashboard.chat.footer.queue_clear_all', {
                  defaultValue: 'Clear all',
                })}
              </Button>
            </span>
          </div>
          {isQueueOpen && (
            <ul className={styles.queueList} data-testid='chat-queue-list'>
              {queuedMessages.map((queuedMessage, index) => (
                <li
                  className={styles.queueRow}
                  key={`${index}-${queuedMessage}`}
                >
                  <span
                    className={styles.queueMessageText}
                    title={queuedMessage}
                    data-testid={`chat-queue-item-text-${index}`}
                  >
                    {queuedMessage}
                  </span>
                  <IconButton
                    size='1'
                    color='error'
                    variant='ghost'
                    icon='trash'
                    data-testid={`chat-queue-remove-${index}`}
                    aria-label={t(
                      'app.dashboard.chat.footer.queue_remove_aria_label',
                      {
                        defaultValue: 'Remove queued message',
                      }
                    )}
                    onClick={() => handleRemoveQueuedMessage(index)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {suggestions.length > 0 && !isLoading && !isStreaming && (
        <FlyChatSuggestions
          onSetMessage={handleSuggestionMessage}
          items={suggestions}
        />
      )}
      <div className={styles.textAreaWrapper}>
        <textarea
          ref={textAreaRef}
          data-testid='chat-input'
          placeholder={t('app.dashboard.chat.footer.input', {
            defaultValue: 'What would you like to do?',
          })}
          autoComplete='off'
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          data-fullstory-unmask
        />
        <div className={styles.footerIcons}>
          <div className={classNames(styles.autoNavWrapper)}>
            <Tooltip
              maxWidth='184px'
              content={t('app.dashboard.chat.footer.auto_nav_description', {
                defaultValue:
                  'This will navigate the interface to the relevant screen according to your chat prompt',
              })}
            >
              <Button
                data-testid='chat-auto-nav-btn'
                size='1'
                variant='surface'
                className='!cursor-pointer pr-fly-3 pl-fly-3'
                color={isActive ? 'accent' : 'neutral'}
                onClick={handleAutoNavClick}
                iconStart='auto_nav'
                iconSize={12}
              >
                {t('app.common.actions.auto_nav', {
                  defaultValue: 'Auto Nav',
                })}
              </Button>
            </Tooltip>
          </div>
          <IconButton
            data-testid='chat-send-user-message-btn'
            size='1'
            variant='soft'
            color='neutral'
            highContrast
            onClick={handleSendMessage}
            disabled={!message.trim() || isLoading}
            iconSize={16}
            icon='arrow_up'
          />
        </div>
      </div>
    </div>
  );
};
